/**
 * The form builder (`specs/M4-V8-forms.md` §2.3, §8 group B).
 *
 * monday's chrome, measured: a **981 × 40** header (*Back to view* 123 × 32 · *Preview* 93 × 32
 * · a centred **Edit / Design / Settings** group 173 × 32 · *Share form* 114 × 32), a **215**
 * wide minimap of **207 × 32** rows with *Add content* at its foot, and a question canvas whose
 * selected card carries a footer bar of *Question settings* · *Logic* · duplicate · delete
 * `[cap:sandbox-builder-edit.json]`.
 *
 * The *Design* tab is the subset we can honour today (colour, logo, progress bar); the rest of
 * monday's Design surface is stored and returned untouched rather than faked.
 */
import { useRef, useState } from 'react';
import { useConfirm } from '@elizmonday/ui';
import type { FormQuestion, FormQuestionKind } from '@elizmonday/shared';
import { AddContentDialog } from './AddContentDialog';
import { QuestionSettingsPanel } from './QuestionSettingsPanel';
import { useFormMutations } from './hooks';
import type { PublicForm } from './types';
import './builder.css';
import '../views/views.css';

export interface BuilderForm extends PublicForm {
  id: string;
  active: boolean;
  url?: string;
}

export type BuilderTab = 'edit' | 'design' | 'settings';

export function FormBuilder({
  form,
  onBack,
  onPreview,
  onShare,
}: {
  form: BuilderForm;
  onBack(): void;
  onPreview(): void;
  onShare(): void;
}) {
  const [tab, setTab] = useState<BuilderTab>('edit');
  const [selected, setSelected] = useState<string | null>(form.questions[0]?.id ?? null);
  const [adding, setAdding] = useState(false);
  const [settingsFor, setSettingsFor] = useState<string | null>(null);
  /**
   * The question being dragged in the minimap (**P-186**). A ref rather than state: the browser's
   * drag controller can dispatch `dragstart` and the first `dragover` in the same task, and a
   * `setState` has not been committed by then.
   */
  const dragging = useRef<string | null>(null);
  const mutations = useFormMutations();
  const confirm = useConfirm();

  /**
   * Reorder (**P-186**). `forms.reorderQuestions` has worked since M4 — it rewrites the order
   * through `updateView`, so the lock, the board permission and the activity row all apply — and
   * `useFormMutations` exposed it to nobody, so questions were stuck in the order they were
   * added and even a new one always landed at the end.
   *
   * The **minimap** is the surface: it is the short, unvirtualised list monday drags in
   * `[cap:sandbox-builder-edit.json]`, and the canvas follows the same array. Whether monday
   * also drags the *cards* is q539 — not captured, so no second drag surface is invented.
   *
   * A dropped question takes the target's index: dragged up it lands before the target, dragged
   * down it lands after it. A drop on itself writes nothing.
   */
  const reorder = (sourceId: string, targetId: string): void => {
    const order = form.questions.map((entry) => entry.id);
    const from = order.indexOf(sourceId);
    const to = order.indexOf(targetId);
    if (from === -1 || to === -1 || from === to) return;
    const next = [...order];
    next.splice(from, 1);
    next.splice(to, 0, sourceId);
    mutations.reorderQuestions.mutate({ formId: form.id, order: next });
  };

  const question = form.questions.find((entry) => entry.id === selected) ?? null;
  const hasName = form.questions.some((entry) => entry.kind === 'name');

  return (
    <div className="ez-builder" data-testid="form-builder" data-tab={tab}>
      <header className="ez-builder__header" data-testid="form-builder-header">
        <div className="ez-builder__left">
          <button type="button" data-testid="builder-back" onClick={onBack}>Back to view</button>
          <button type="button" data-testid="builder-preview" onClick={onPreview}>Preview</button>
        </div>
        <div className="ez-builder__tabs" role="tablist" aria-label="Form builder">
          {(['edit', 'design', 'settings'] as const).map((entry) => (
            <button
              key={entry}
              type="button"
              role="tab"
              aria-selected={tab === entry}
              data-testid={`builder-tab-${entry}`}
              onClick={() => setTab(entry)}
            >
              {entry === 'edit' ? 'Edit' : entry === 'design' ? 'Design' : 'Settings'}
            </button>
          ))}
        </div>
        <div className="ez-builder__right">
          <button type="button" className="ez-builder__share" data-testid="builder-share" onClick={onShare}>
            Share form
          </button>
        </div>
      </header>

      <div className="ez-builder__body">
        <nav className="ez-builder__minimap" aria-label="Questions" data-testid="form-minimap">
          <h2>Content</h2>
          <ul>
            {form.questions.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  className="ez-builder__minimap-row"
                  data-testid={`minimap-question-${entry.id}`}
                  data-selected={entry.id === selected || undefined}
                  draggable
                  onClick={() => setSelected(entry.id)}
                  onDragStart={(event) => {
                    dragging.current = entry.id;
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', entry.id);
                  }}
                  onDragEnd={() => {
                    dragging.current = null;
                  }}
                  onDragOver={(event) => {
                    if (dragging.current === null || dragging.current === entry.id) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(event) => {
                    if (dragging.current === null || dragging.current === entry.id) return;
                    event.preventDefault();
                    const source = dragging.current;
                    dragging.current = null;
                    reorder(source, entry.id);
                  }}
                  onKeyDown={(event) => {
                    // The keyboard half of the same gesture: Alt+↑/↓ moves the question a place.
                    if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
                    event.preventDefault();
                    const order = form.questions.map((row) => row.id);
                    const at = order.indexOf(entry.id);
                    const target = order[at + (event.key === 'ArrowUp' ? -1 : 1)];
                    if (target !== undefined) reorder(entry.id, target);
                  }}
                >
                  <span className="ez-builder__minimap-grip" data-testid={`question-drag-${entry.id}`} aria-hidden="true">
                    ⠿
                  </span>
                  <span className="ez-builder__minimap-label">{entry.title}</span>
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="ez-builder__add"
            data-testid="builder-add-content"
            onClick={() => setAdding(true)}
          >
            + Add content
          </button>
        </nav>

        {tab === 'edit' ? (
          <div className="ez-builder__canvas" data-testid="form-canvas">
            {form.questions.map((entry) => (
              <QuestionCard
                key={entry.id}
                question={entry}
                selected={entry.id === selected}
                onSelect={() => setSelected(entry.id)}
                onTitle={(title) =>
                  mutations.updateQuestion.mutate({ formId: form.id, questionId: entry.id, title })
                }
                onDescription={(description) =>
                  mutations.updateQuestion.mutate({ formId: form.id, questionId: entry.id, description })
                }
                onSettings={() => { setSelected(entry.id); setSettingsFor(entry.id); }}
                onDelete={() => {
                  /*
                   * P-012, and a **deliberate deviation**: monday's form builder removes a
                   * question on the first click. Ours asks, because deleting a question here
                   * is not the same operation — it takes the question's settings, its
                   * validation and its link to the board column with it, there is no undo,
                   * and the button sits inside a card the person has just clicked to select.
                   * Recorded in `docs/open-questions.md` (q7c-1) rather than assumed.
                   */
                  void (async () => {
                    const ok = await confirm({
                      title: `Delete “${entry.title}”?`,
                      body: 'The question and its settings are removed from the form. Answers already submitted are kept.',
                      confirmLabel: 'Delete question',
                      danger: true,
                    });
                    if (!ok) return;
                    mutations.removeQuestion.mutate({ formId: form.id, questionId: entry.id });
                  })();
                }}
              />
            ))}
          </div>
        ) : null}

        {tab === 'design' ? <DesignTab form={form} /> : null}
        {tab === 'settings' ? <SettingsTab form={form} /> : null}

        {settingsFor !== null && question !== null ? (
          <QuestionSettingsPanel
            question={question}
            data-testid="question-settings-panel"
            onClose={() => setSettingsFor(null)}
            onChange={(patch) =>
              mutations.updateQuestion.mutate({ formId: form.id, questionId: question.id, ...patch })
            }
          />
        ) : null}
      </div>

      {adding ? (
        <AddContentDialog
          hasNameQuestion={hasName}
          onClose={() => setAdding(false)}
          onAdd={(kind: FormQuestionKind, label: string) => {
            setAdding(false);
            mutations.addQuestion.mutate({ formId: form.id, kind, title: label });
          }}
        />
      ) : null}
    </div>
  );
}

function QuestionCard({
  question,
  selected,
  onSelect,
  onTitle,
  onDescription,
  onSettings,
  onDelete,
}: {
  question: FormQuestion;
  selected: boolean;
  onSelect(): void;
  onTitle(title: string): void;
  onDescription(description: string): void;
  onSettings(): void;
  onDelete(): void;
}) {
  return (
    <article
      className="ez-question-card"
      data-selected={selected || undefined}
      data-testid={`question-card-${question.id}`}
      onClick={onSelect}
    >
      <input
        className="ez-question-card__title"
        defaultValue={question.title}
        aria-label={`Question title: ${question.title}`}
        data-testid={`question-title-${question.id}`}
        onBlur={(event) => {
          const next = event.target.value.trim();
          if (next !== '' && next !== question.title) onTitle(next);
        }}
      />
      <input
        className="ez-question-card__description"
        defaultValue={question.description ?? ''}
        placeholder="Add a description"
        aria-label={`Description for ${question.title}`}
        data-testid={`question-description-${question.id}`}
        onBlur={(event) => {
          if (event.target.value !== (question.description ?? '')) onDescription(event.target.value);
        }}
      />
      {question.options.length > 0 ? (
        <ul className="ez-question-card__options">
          {question.options.map((option) => (
            <li key={option.value}>{option.label}</li>
          ))}
        </ul>
      ) : null}
      {selected ? (
        <footer className="ez-question-card__foot">
          <button type="button" data-testid={`question-settings-${question.id}`} onClick={onSettings}>
            Question settings
          </button>
          <button type="button" data-testid={`question-logic-${question.id}`} disabled title="Coming with M4.V8b">
            Logic
          </button>
          <button
            type="button"
            className="ez-question-card__delete"
            data-testid={`question-delete-${question.id}`}
            onClick={(event) => { event.stopPropagation(); onDelete(); }}
          >
            Delete question
          </button>
        </footer>
      ) : null}
    </article>
  );
}

function DesignTab({ form }: { form: BuilderForm }) {
  const mutations = useFormMutations();
  return (
    <div className="ez-builder__pane" data-testid="form-design">
      <label>
        <span>Color theme</span>
        <input
          type="color"
          data-testid="design-header-color"
          // No default literal: an unset colour is the browser's, and F7's rule bans raw hex.
          {...(form.appearance.headerColor === null ? {} : { defaultValue: form.appearance.headerColor })}
          onBlur={(event) =>
            mutations.update.mutate({ formId: form.id, appearance: { headerColor: event.target.value } })
          }
        />
      </label>
      <label>
        <span>Logo URL</span>
        <input
          type="url"
          data-testid="design-logo"
          defaultValue={form.appearance.logoUrl ?? ''}
          onBlur={(event) =>
            mutations.update.mutate({ formId: form.id, appearance: { logoUrl: event.target.value || null } })
          }
        />
      </label>
      <label>
        <input
          type="checkbox"
          data-testid="design-hide-branding"
          defaultChecked={form.settings.hideBranding}
          onChange={(event) =>
            mutations.update.mutate({ formId: form.id, settings: { hideBranding: event.target.checked } })
          }
        />
        <span>Hide product branding</span>
      </label>
    </div>
  );
}

function SettingsTab({ form }: { form: BuilderForm }) {
  const mutations = useFormMutations();
  const set = (settings: Record<string, unknown>) => mutations.update.mutate({ formId: form.id, settings });
  return (
    <div className="ez-builder__pane" data-testid="form-settings">
      <h3>General settings</h3>
      <label>
        <input
          type="checkbox"
          data-testid="settings-submit-another"
          defaultChecked={form.settings.submitAnother !== 'none'}
          onChange={(event) => set({ submitAnother: event.target.checked ? 'click' : 'none' })}
        />
        <span>Submit multiple forms</span>
      </label>
      <label>
        <input
          type="checkbox"
          data-testid="settings-anonymous"
          defaultChecked={form.settings.isAnonymous}
          onChange={(event) => set({ isAnonymous: event.target.checked })}
        />
        <span>Anonymous form</span>
      </label>

      <h3>After submission</h3>
      <label>
        <span>Success message</span>
        <input
          data-testid="settings-success-title"
          defaultValue={form.settings.successTitle ?? ''}
          onBlur={(event) => set({ successTitle: event.target.value || null })}
        />
      </label>
      <label>
        <span>Redirect URL</span>
        <input
          data-testid="settings-redirect"
          defaultValue={form.settings.redirect.url ?? ''}
          onBlur={(event) =>
            set({ redirect: { enabled: event.target.value !== '', url: event.target.value || null } })
          }
        />
      </label>

      <h3>Board</h3>
      <label>
        <input
          type="checkbox"
          data-testid="settings-sync-titles"
          defaultChecked={form.settings.syncQuestionAndColumnTitles}
          onChange={(event) => set({ syncQuestionAndColumnTitles: event.target.checked })}
        />
        <span>Sync questions and column titles</span>
      </label>
    </div>
  );
}
